import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ErrorLabelView from './ErrorLabelView';
import Button from '../components/buttons/Button';
import { SubmitHandler, useForm } from 'react-hook-form';
import { TransactionSearchRequestContextActions } from '../context/actions/transactionSearchRequestContextActions';
import { useAppContext } from '../context/AppContext';
import AmountInputField from './AmountInputField';
import CurrencySelectInputField from './CurrencySelectInputField';
import PaymentMethodSelectInputField from './PaymentMethodSelectInputField';
import IdentifierInputField from "./IdentifierInputField";
import TransactionDateDatePicker from "./TransactionDateDatePicker";
import {
  funcValidateAmount,
  funcValidateCurrency,
  funcValidateDate, funcValidateEmail,
  funcValidateIdentifier,
  funcValidatePaymentMethod
} from "./UtilsValidations";


export type ShowMainTransactionDataFormViewInput = {
  amount: string;
  identifier: string;
  currencyCode: { label: string; value: string };
  transactionDate: string | undefined;
  paymentMethod: { label: string; value: string };
  email: string
};

type ShowMainTransactionDataFormViewProps = {
  onSubmit: () => void;
};

const ShowMainTransactionDataFormView = ({
                                           onSubmit
                                         }: ShowMainTransactionDataFormViewProps) => {
  const [formLocks, setFormLocks] = useState(false);
  const { transactionSearchRequestDispatch } = useAppContext();
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
  } = useForm<ShowMainTransactionDataFormViewInput>();

  const onSubmitHandler: SubmitHandler<
      ShowMainTransactionDataFormViewInput
  > = ({ amount, identifier, currencyCode, transactionDate, paymentMethod, email }) => {
    setFormLocks(false);
    transactionSearchRequestDispatch({
      type: TransactionSearchRequestContextActions.setFormData,
      data: {
        amount,
        identifier,
        currency: currencyCode == null ? '' : currencyCode.value,
        transactionDate,
        paymentMethod: paymentMethod == null ? '' : paymentMethod.value,
        email
      },
    });
    onSubmit();
  };

  const onSubmitHandlerMemoized = useCallback(onSubmitHandler, [transactionSearchRequestDispatch, onSubmit]);


  return (
    <form onSubmit={handleSubmit(onSubmitHandlerMemoized)}>
      <div className="main-form-container">

        <div className="main-form-row">
          <div className="form-row-transactionIdentifier">
            <label htmlFor="identifierInput">
              <FormattedMessage id="formData.transactionId"/>
            </label>
            <IdentifierInputField
                {...register('identifier', {
                  validate: (value, {transactionDate, amount, currencyCode, paymentMethod, email}) =>
                      funcValidateIdentifier(value, transactionDate, amount, currencyCode, paymentMethod, email)
                })}
            />
            {errors.identifier && (
                <ErrorLabelView message={errors.identifier.message}/>
            )}
          </div>
        </div>

        <div>
          <div className="main-form-row">
            <div className="form-row-separator">
            <FormattedMessage id="formData.separator"/>
            </div>
          </div>
        </div>

        <div className="main-form-row">
          <div className="form-row-date">
            <label htmlFor="date-picker-input">
              <FormattedMessage id="formData.transactionDate"/>
            </label>
            <TransactionDateDatePicker
                name="transactionDate"
                control={control}
                rules={{
                  validate: (value, {identifier, amount ,paymentMethod, email, currencyCode}) =>
                      funcValidateDate(value, identifier, amount, currencyCode, paymentMethod, email)
                }}
            />
            {errors.transactionDate && (
                <ErrorLabelView message={errors.transactionDate.message}/>
            )}
          </div>
        </div>

        <div className="main-form-row">
          <div className="form-row-amount">
            <label htmlFor="amountInput">
              <FormattedMessage id="formData.amount"/>
            </label>
            <AmountInputField
                {...register('amount', {
                  validate: (value, {identifier, email, currencyCode, paymentMethod, transactionDate}) =>
                      funcValidateAmount(value, identifier, email, currencyCode, paymentMethod, transactionDate),
                  pattern: {
                    value: /\d*/,
                    message: 'formData.validation.decimalPriceRequired',
                  },
                })}
            />
            {errors.amount && (
                <ErrorLabelView message={errors.amount.message}/>
            )}
          </div>
          <div className="form-row-currency">
            <label htmlFor="currency-select-input">
              <FormattedMessage id="formData.currency"/>
            </label>
            <CurrencySelectInputField
                control={control}
                name="currencyCode"
                rules={{
                  validate: (value, {identifier, amount, paymentMethod, email, transactionDate}) =>
                      funcValidateCurrency(value, identifier, transactionDate, amount, paymentMethod, email)
                }}
            />
            {errors.currencyCode && (
                <ErrorLabelView message="formData.validation.required"/>
            )}
          </div>
        </div>
        <div>
          <p></p>
          <p></p>
        </div>
        <div className="main-form-row">
          <div className="form-row-payment-method">
            <label htmlFor="payment-method-select-input">
              <FormattedMessage id="formData.paymentMethod"/>
            </label>
            <PaymentMethodSelectInputField
                control={control}
                name="paymentMethod"
                rules={{
                  validate: (value, {identifier, amount, currencyCode, email, transactionDate}) =>
                      funcValidatePaymentMethod(value, identifier, amount, currencyCode, email, transactionDate)
                }}
            />
            {errors.paymentMethod && (
                <ErrorLabelView message="formData.validation.required"/>
            )}
          </div>
        </div>
        <div className="main-form-row">
          <div className="form-row-email">
            <label htmlFor="payment-method-select-input">
              <FormattedMessage id="formData.email"/>
            </label>
            <input
                {...register('email', {
                  validate: (value, {identifier, amount, currencyCode, paymentMethod, transactionDate}) =>
                      funcValidateEmail(value, identifier, amount, currencyCode, paymentMethod, transactionDate)
                })}
            />
            {errors.email && (
                <ErrorLabelView message={errors.email.message}/>
            )}
          </div>
        </div>
        <div className="main-form-row">
          <Button
              name="send_button"
              type="submit"
              text={<FormattedMessage id="formData.button"/>}
              disabled={formLocks}
          />
        </div>
      </div>
    </form>
  );
};

export default ShowMainTransactionDataFormView;
